import CaseAutocomplete from "components/CaseAutocomplete";
import { StateHandle } from "StateHandle";
import theme, { EllipsisSX } from "theme";
import { P } from "theme/typography";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Close, CodeSharp, ContentCopy, Done } from "@mui/icons-material";
import { runGqlQuery } from "gqlClient";
import { GetScriptInputsDocument, type SearchCasesResult } from "generated/graphql";
import useCopy from "helpers/hooks/useCopy";
import { useState } from "react";
import { useSnackbar } from "notistack";

const ScriptExecuteCaseList = ({
  lensId,
  casesHandle,
  scriptName,
  label,
}: {
  lensId: number;
  casesHandle: StateHandle<SearchCasesResult[]>;
  scriptName: string;
  label?: string;
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isCopied, copy] = useCopy();
  const [lastCopiedCaseId, setLastCopiedCaseId] = useState<number | undefined>();
  const getInput = async (caseId: number, type: "jsonTestCase" | "json" | "groovy") => {
    try {
      const r = await runGqlQuery(GetScriptInputsDocument, {
        caseIds: [caseId],
        lensId,
        scriptName,
        scriptParameters: [],
      }).then((r) => r.lens.scripts.getScriptInputs);
      setLastCopiedCaseId(caseId);
      const firstResult = r[0];
      if (firstResult === undefined) return copy("");
      const inputToCopy =
        type === "json"
          ? firstResult.inputMapJson
          : type === "jsonTestCase"
            ? JSON.stringify({ input: JSON.parse(firstResult.inputMapJson ?? "{}"), output: {} })
            : firstResult.inputMapGroovy;
      copy(inputToCopy ?? "");
    } catch (e) {
      enqueueSnackbar("Error retrieving input", { variant: "error" });
    }
  };

  return (
    <>
      <CaseAutocomplete
        label={label ?? "Case"}
        clearOnChange
        lensId={lensId}
        onChange={(lensCase) => {
          if (casesHandle[0]) {
            casesHandle[1]((cases) => (lensCase ? [...cases, lensCase] : cases));
          }
        }}
      />
      {casesHandle[0].length > 0 && (
        <Box sx={{ overflowY: "auto" }}>
          {casesHandle[0].map((c) => (
            <Box
              sx={{
                p: 0.5,
                backgroundColor: theme.palette.patternSecondary.main,
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                mb: 0.5,
              }}
              key={c.caseId}>
              <P variant="subtitle2" sx={{ color: "white", ...EllipsisSX }}>
                {c.claimantName}
              </P>
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title="Copy JSON Test Case">
                <IconButton size="small" sx={{ color: "white" }} onClick={() => getInput(c.caseId, "jsonTestCase")}>
                  {isCopied && lastCopiedCaseId === c.caseId ? <Done /> : <CodeSharp />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy Input Groovy Map">
                <IconButton size="small" sx={{ color: "white" }} onClick={() => getInput(c.caseId, "groovy")}>
                  {isCopied && lastCopiedCaseId === c.caseId ? <Done /> : <ContentCopy />}
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                sx={{ color: "white" }}
                onClick={() => {
                  casesHandle[1]((cases) => {
                    const foundIndex = cases.findIndex((caseParam) => caseParam.caseId === c.caseId);
                    const newCases = cases.slice();

                    if (foundIndex > -1) {
                      newCases.splice(foundIndex, 1);
                    }

                    return newCases;
                  });
                }}>
                <Close />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

export default ScriptExecuteCaseList;
